"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "../AnimatedSection";
import { Calendar, Ticket, QrCode, Beer, Crown, DollarSign } from "lucide-react";

const steps = [
  {
    icon: Calendar,
    en: { kicker: "Tuesday", title: "Announce", body: "Create the event in the admin. Your public venue site updates instantly." },
    es: { kicker: "Martes", title: "Anuncias", body: "Creas el evento en el admin. Tu sitio público se actualiza al instante." },
  },
  {
    icon: Ticket,
    en: { kicker: "Wed → Fri", title: "Sell", body: "Tickets sell through your branded checkout. Promoters share their tracked links." },
    es: { kicker: "Mié → Vie", title: "Vendes", body: "Los tickets se venden por tu checkout. Los promotores comparten sus links rastreados." },
  },
  {
    icon: QrCode,
    en: { kicker: "9:00 PM", title: "Doors Open", body: "Scanners check in guests over WiFi or mobile data. Duplicates get caught; the line keeps moving." },
    es: { kicker: "9:00 PM", title: "Abren Puertas", body: "Los scanners hacen check-in con WiFi o datos móviles. Los duplicados se detectan; la fila sigue avanzando." },
  },
  {
    icon: Beer,
    en: { kicker: "10:30 PM", title: "Bar Goes Live", body: "Bartenders open tabs, fire orders. Inventory and pours track in real time." },
    es: { kicker: "10:30 PM", title: "La Barra Arranca", body: "Los bartenders abren tabs y disparan órdenes. Inventario y onzas en tiempo real." },
  },
  {
    icon: Crown,
    en: { kicker: "11:30 PM", title: "VIP Fills", body: "Hosts seat tables, fire bottles, drag the floor map. Supervisor PIN for closes and comps." },
    es: { kicker: "11:30 PM", title: "Llega VIP", body: "Los hosts sientan mesas, disparan botellas, mueven el mapa. Supervisor PIN para cierres y comps." },
  },
  {
    icon: DollarSign,
    en: { kicker: "Monday", title: "Promoters Settled", body: "Each promoter's commission is already calculated. Pay them your way and mark it paid." },
    es: { kicker: "Lunes", title: "Pago a Promotores", body: "La comisión de cada promotor ya está calculada. Les pagas como prefieras y la marcas pagada." },
  },
];

export default function NightlifeStack() {
  const { t } = useLang();

  return (
    <section className="relative py-24 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      {/* Soft accent glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#7C3AED]/15 rounded-full blur-[140px] pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6] mb-4">
            {t("How a Friday runs on Redy", "Cómo corre un viernes en Redy")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight text-white leading-[1.05]">
            {t("From announce to settle.", "Del anuncio al cierre.")}
            <br />
            <span className="text-white/30">
              {t("One system. Zero spreadsheets.", "Un sistema. Cero hojas de cálculo.")}
            </span>
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const copy = step;
            return (
              <AnimatedElement
                key={i}
                delay={0.07 * i}
                className="relative bg-white/[0.03] border border-white/10 rounded-[24px] p-7"
              >
                {/* Step number */}
                <div className="absolute top-5 right-5 text-[40px] font-black text-white/[0.06] leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>

                <div className="w-11 h-11 rounded-[14px] bg-white/[0.06] border border-white/10 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#C4B5FD]" />
                </div>

                <div className="text-[10px] font-bold tracking-widest uppercase text-[#F472B6] mb-1.5">
                  {t(copy.en.kicker, copy.es.kicker)}
                </div>
                <h3 className="text-[20px] font-black text-white mb-2">
                  {t(copy.en.title, copy.es.title)}
                </h3>
                <p className="text-[14px] font-medium text-white/55 leading-relaxed">
                  {t(copy.en.body, copy.es.body)}
                </p>
              </AnimatedElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
